import {
  isToday,
  parseISO,
  isYesterday,
  isSameDay,
  subDays,
  endOfDay,
  isBefore,
  startOfDay,
} from "date-fns";

const locale = "en-US";

export const totalCalcFunc = (rows, field) =>
  rows.reduce((sum, row) => Number(row.original[field] || 0) + Number(sum), 0);

export function formatCurrency(number, currency = "USD") {
  if (!number) return "None";
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: currency,
    currencyDisplay: "narrowSymbol",
  })
    .format(number)
    .replace(/\D00(?=\D*$)/, "");
}
export function formatDwellingCurrency(number, currency = "USD") {
  if (!number) return "";
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: currency,
    currencyDisplay: "narrowSymbol",
  })
    .format(number)
    .replace(/\D00(?=\D*$)/, "");
}

export function currencyFormat(number, currency = "USD") {
  if (!number) return "";
  const num = number.replace(/\D/g, "");
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: currency,
    currencyDisplay: "narrowSymbol",
  })
    .format(num)
    .replace(/\D00(?=\D*$)/, "");
}
export function roundToThousand(num) {
  return Math.round(num / 1000) * 1000;
}

export function roundToHundredth(num) {
  return Math.round((num + Number.EPSILON) * 100) / 100;
}

export function formatNumber(num) {
  return new Intl.NumberFormat(locale).format(Number(num));
}

export function getNumbersOnly(value) {
  return ("" + (value || "")).replace(/\D/g, "");
}

export function formatPhone(value: string | number) {
  const cleaned = getNumbersOnly(value);
  const match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    const intlCode = match[1] ? "+1 " : "";
    return [intlCode, match[2], "-", match[3], "-", match[4]].join("");
  }
  return null;
}

export function numberToFixed(total, digits) {
  return formatNumber(total.toFixed(digits));
}

export function kFormatter(num: number) {
  return (
    "$" +
    (Math.abs(num) > 999
      ? Math.sign(num) * (Math.round(Math.abs(num) / 100) / 10) + "k"
      : Math.sign(num) * Math.abs(num))
  );
}

export function numberDriver(index) {
  switch (index) {
    case 1:
      return "First";
    case 2:
      return "Second";
    case 3:
      return "Third";
    case 4:
      return "Fourth";
    case 5:
      return "Fifth";
    default:
      if (index % 10 === 1 && index % 100 !== 11) {
        return index + "st";
      }
      if (index % 10 === 2 && index % 100 !== 12) {
        return index + "nd";
      }
      if (index % 10 === 3 && index % 100 !== 13) {
        return index + "rd";
      }
      return index + "th";
  }
}

export function formatTitleCase(txt) {
  if (!txt) return "";
  return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
}

export function getDwellingAmount(type, amount) {
  switch (type) {
    case "C":
      if (amount < 30000) {
        return 30000;
      } else {
        return amount;
      }
    case "D":
    case "T":
    case "X":
      if (amount < 100000) {
        return 100000;
      } else {
        return amount;
      }
    default:
      return amount;
  }
}
export function getHeatingUpdate(yearOfConstruction) {
  if (yearOfConstruction < new Date().getFullYear() - 20) {
    return new Date().getFullYear() - 20;
  } else {
    return yearOfConstruction;
  }
}

export function phoneFormatter(input) {
  //returns (###) ###-####
  input = input.replace(/\D/g, "");
  const size = input.length;
  if (size > 0) {
    input = "(" + input;
  }
  if (size > 3) {
    input = input.slice(0, 4) + ") " + input.slice(4, 11);
  }
  if (size > 6) {
    input = input.slice(0, 9) + "-" + input.slice(9);
  }
  return input;
}

export function getReferralToken(user) {
  const referral_token = user.user_agency.find(
    (ua) => ua.agency.domain === window.location.hostname,
  )?.referral_token;
  return referral_token;
}

export const getDay = (date) => {
  return isToday(parseISO(date))
    ? "Today"
    : isYesterday(parseISO(date))
    ? "Yesterday"
    : isSameDay(new Date(date), subDays(new Date(), 2))
    ? "2 days ago"
    : isSameDay(new Date(date), subDays(new Date(), 3))
    ? "3 days ago"
    : "3+ days ago";
};

export const invalidDates = (start, end, setError) => {
  if (
    isBefore(
      new Date(startOfDay(new Date(start))),
      new Date(endOfDay(new Date(end))),
    ) === false
  ) {
    setError(true);
    return true;
  } else return false;
};

export const ensureHttps = (url) => {
  if (url) {
    return url?.includes("https://") ? url : "https://" + url;
  }
  return "";
};

export const replaceWhiteSpaceWithPlus = (value) => {
  return value.replace(/\s/g, "+");
};

export function formatSplitCase(input: string) {
  const inputSplit = input
    .split("_")
    .map((s) => s.replace("_", " "))
    .join(" ");
  return inputSplit.charAt(0).toUpperCase() + inputSplit.slice(1);
}

// service request functions
export function formatPolicyType(policyType: string) {
  switch (policyType) {
    case "auto":
      return "Auto";
    case "home":
      return "Home";
    case "life":
      return "Life";
    case "commercial":
      return "Commercial";
    case "other":
      return "Other";
    default:
      return "Unknown";
  }
}
export function formatRequestType(requestType: string) {
  switch (requestType) {
    case "policy_change":
      return "Policy Change";
    case "mortgage_change":
      return "Mortgage Change";
    case "billing":
      return "Billing";
    case "other":
      return "Other";
    default:
      return "Unknown";
  }
}
  
export const getServiceRequestStatus = (status: string) => {
  switch (status) {
    case "new":
      return "New Service Request";
    case "in_progress":
      return "In Progress";
    case "pending_response":
      return "Pending Response";
    case "closed":
      return "Completed";
    default:
      return "Service Request";
  }
};

export const getServiceRequestColor = (status: string) => {
  let color;
  switch (status) {
    case "new":
      color = "#2ED272";
      break;
    case "in_progress":
      color = "blue";
      break;
    case "pending_response":
      color = "#FFCC00";
      break;
    case "closed":
      color = "#757fa4";
      break;
    default:
      color = "#757575";
  }
  return color;
};

export const getServiceRequestBgColor = (status: string) => {
  // colors and bgcolors for pending_response should match chip on list page use switch
  let bgcolor;
  switch (status) {
    case "new":
      bgcolor = "#2ED27221";
      break;
    case "in_progress":
      bgcolor = "#d4ddff";
      break;
    case "pending_response":
      bgcolor = "#FFCC002B";
      break;
    case "closed":
      bgcolor = "#eeeff5";
      break;
    default:
      bgcolor = "#F5F5F5";
  }
  return bgcolor;
};